import { useState } from "react";
import C from "classnames";

import { Tag } from "primereact/tag";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";

import { useMediaQuery, MEDIA_QUERY_BELOW_XL } from "sj-ui-toolkit/src/hooks";

import { useProxyRequests } from "../../../contexts/SandboxHooks";
import { ProxyRequest } from "../../../contexts/Proxy/ProxyTypes";

import S from "./ProxyHistory.module.scss";

const ProxyHistory = () => {
  const { requests } = useProxyRequests();
  const [selectedRequest, setSelectedRequest] = useState<ProxyRequest | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const isStacked = useMediaQuery(MEDIA_QUERY_BELOW_XL);

  const getStatusColor = (statusCode?: number) => {
    if (!statusCode) return "text-muted-aaa";
    if (statusCode >= 200 && statusCode < 300) return "text-success";
    if (statusCode >= 300 && statusCode < 400) return "text-info";
    if (statusCode >= 400 && statusCode < 500) return "text-warning";
    if (statusCode >= 500) return "text-danger";
    return "text-muted";
  };

  const formatTime = (date?: string | number | Date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  // If requests is null/undefined, show a loading message
  if (!requests) {
    return <div className={S.proxyLoading}>Loading requests...</div>;
  }

  // Filter requests based on search query
  const filteredRequests = requests.filter(request => {
    const query = searchQuery.toLowerCase();
    const matchesDescription = request.description.toLowerCase().includes(query);
    return matchesDescription;
  });

  // Figure out which request to show details for
  const currentRequest = selectedRequest ?? filteredRequests[0] ?? null;

  return (
    <div className={S.proxyWrapper}>
      {requests?.length === 0 ? (
        <div className={S.proxyEmpty}>
          <p>No requests captured yet. Enable interception to start capturing requests.</p>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {isStacked && (
            <div className={S.searchBox}>
              <Dropdown
                options={requests}
                optionLabel="description"
                dataKey="id"
                value={currentRequest}
                onChange={e => setSelectedRequest(e.value)}
                filter
                placeholder="Select a request"
                aria-label="Select a request"
                data-testid="proxy-history-mobile-select"
                className="w-full"
              />
            </div>
          )}
          <div className="flex flex-1 min-h-0">
            {/* Left Panel: Requests List */}
            {!isStacked && (
              <div className={S.requestsListPanel}>
                <div className={S.searchBox}>
                  <InputText
                    placeholder="Search requests..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full"
                    data-testid="proxy-history-search-input"
                    aria-label="Search requests"
                  />
                </div>
                <div className="w-full">
                  {filteredRequests?.map(request => (
                    <div
                      key={request.id}
                      className={C(S.requestItem, selectedRequest?.id === request.id && S.active)}
                      data-testid={`proxy-request-${request.id}`}
                      onClick={() => setSelectedRequest(request)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          setSelectedRequest(request);
                        }
                      }}
                    >
                      <div className={S.requestMethod}>
                        <Tag value={request.description.split(" ")[0]} />
                      </div>
                      <div className={S.requestPath}>{request.description.split(" ")[1]}</div>
                      <div
                        className={C(S.statusCode, getStatusColor(request.response?.statusCode))}
                      >
                        {request.response?.statusCode || "pending"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Right Panel: Request Details */}
            <div className={C(isStacked ? "w-full" : "w-3/4", S.requestDetailsPanel)}>
              {currentRequest ? (
                <div className={S.requestDetails}>
                  {/* Header */}
                  <div className={S.requestHeader}>
                    <div>
                      {!isStacked && <h3 className="atlas-h4">{currentRequest.description}</h3>}
                      <p className={C("body-sm text-muted", S.generatedAt)}>
                        {formatTime(currentRequest.generatedAt)}
                      </p>
                    </div>
                    {currentRequest.editedText && <div className={S.editedBadge}>Edited</div>}
                  </div>

                  {/* Request and Response Text */}
                  <div className={S.requestResponseContainer}>
                    {/* Request Section */}
                    <div className={S.section}>
                      <h4 className="atlas-h5">Request</h4>
                      <pre tabIndex={0} className={C(S.textBox, "atlas-code")}>
                        {currentRequest.text}
                      </pre>
                    </div>

                    {/* Edited Request Section */}
                    {currentRequest.editedText && (
                      <div className={S.section}>
                        <h4 className="atlas-h5">Edited Request</h4>
                        <pre tabIndex={0} className={C(S.textBox, "atlas-code")}>
                          {currentRequest.editedText}
                        </pre>
                      </div>
                    )}

                    {/* Response Section */}
                    {currentRequest.response && (
                      <div className={S.section}>
                        <h4 className="atlas-h5">
                          Response ({currentRequest.response.statusCode})
                        </h4>
                        <pre tabIndex={0} className={C(S.textBox, "atlas-code")}>
                          {currentRequest.response.text}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={S.requestEmpty}>
                  <p>Select a request to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProxyHistory;
